import { DateTime } from 'luxon';
import { SUPPORTED_DATE_FORMATS } from 'constants';

// Import store to access user timezone
let store = null;
try {
    // Dynamic import to avoid circular dependencies
    store = require('../state/store').default;
} catch (e) {
    // Store not available, will use default
}

/**
 * Get user timezone from Redux store or default to 'Asia/Kolkata'
 * @param {string} timezone - Optional explicit timezone override
 * @returns {string} - User's timezone or default
 */
function getUserTimezone(timezone = null) {
    if (timezone) return timezone;
    
    try {
        if (store) {
            const state = store.getState();
            return state?.auth?.userInfo?.timezone || 'Asia/Kolkata';
        }
    } catch (e) {
        // Store not available or error accessing it
    }
    return 'Asia/Kolkata';
}

/**
 * Parse a date string (ISO or other formats) and convert it to the specified format
 * @param {string} format - Target format (e.g., 'yyyy-MM-dd', 'MM/dd/yyyy')
 * @param {string} dateString - Date string in ISO format (YYYY-MM-DD) or other supported formats
 * @param {string} timezone - Optional timezone (defaults to user's timezone)
 * @returns {string|null} - Formatted date string or null if invalid
 */
function parseDate(format, dateString, timezone = null) {
    if (!dateString) return null;
    
    const tz = getUserTimezone(timezone);
    
    // First try ISO format (YYYY-MM-DD) - this is now the standard format from server
    let parsedDate = DateTime.fromISO(dateString, { zone: tz });
    if (parsedDate.isValid) {
        return parsedDate.toFormat(format);
    }
    
    // Fallback to other supported formats for backward compatibility
    for (const checkFormat of SUPPORTED_DATE_FORMATS) {
        parsedDate = DateTime.fromFormat(dateString, checkFormat, { zone: tz });
        if (parsedDate.isValid) {
            return parsedDate.toFormat(format);
        }
    }
    return null;
}

/**
 * Format an ISO date string for display
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @param {string} format - Display format (default: 'MM/dd/yyyy')
 * @param {string} timezone - Optional timezone (defaults to user's timezone)
 * @returns {string|null} - Formatted date string or null if invalid
 */
function formatDate(dateString, format = 'MM/dd/yyyy', timezone = null) {
    if (!dateString) return null;
    const tz = getUserTimezone(timezone);
    const parsedDate = DateTime.fromISO(dateString, { zone: tz });
    if (parsedDate.isValid) {
        return parsedDate.toFormat(format);
    }
    return null;
}

/**
 * Format an ISO date string for display with locale support
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @param {object} options - Intl.DateTimeFormat options
 * @param {string} timezone - Optional timezone (defaults to user's timezone)
 * @returns {string|null} - Formatted date string or null if invalid
 */
function formatDateLocale(dateString, options = {}, timezone = null) {
    if (!dateString) return null;
    const tz = getUserTimezone(timezone);
    const parsedDate = DateTime.fromISO(dateString, { zone: tz });
    if (parsedDate.isValid) {
        return parsedDate.toLocaleString(options);
    }
    return null;
}

export {
    parseDate,
    formatDate,
    formatDateLocale,
    getUserTimezone,
};